import type {
  CaptureQueueBatch,
  CaptureQueueSnapshot,
  EnqueueCapturePhoto,
  QueuedCapturePhoto,
} from "@/types/capture"

const DATABASE_NAME = "ninjanizr-capture-queue"
const DATABASE_VERSION = 1
const BATCHES_STORE = "batches"
const PHOTOS_STORE = "photos"
const PHOTOS_BY_ACCOUNT_BATCH_GROUP_TIME = "byAccountBatchGroupCapturedAt"
const RESERVED_BYTES = 1024 * 1024
const HIGHEST_KEY_PART = "\uffff"
const PERSISTENCE_ERROR_NAMES = new Set([
  "AbortError",
  "InvalidStateError",
  "NotReadableError",
  "QuotaExceededError",
  "SecurityError",
  "TimeoutError",
  "UnknownError",
  "VersionError",
])

interface StoredCaptureQueueBatch extends CaptureQueueBatch {
  accountId: string
}

interface StoredQueuedCapturePhoto extends QueuedCapturePhoto {
  accountId: string
}

export class CaptureQueueFullError extends Error {
  constructor(message = "This device cannot safely store another capture") {
    super(message)
    this.name = "CaptureQueueFullError"
  }
}

/** Persist server batch/group transitions even when no new photo is taken. */
export async function saveBatch(accountId: string, batch: CaptureQueueBatch): Promise<void> {
  validateAccountAndBatch(accountId, batch)
  await runTransaction(BATCHES_STORE, "readwrite", (transaction) => {
    transaction.objectStore(BATCHES_STORE).put({ accountId, ...batch })
  })
}

export async function enqueuePhoto(
  accountId: string,
  input: EnqueueCapturePhoto,
): Promise<QueuedCapturePhoto> {
  validateAccountAndBatch(accountId, input.batch)
  requireIdentifier(input.groupId, "group ID")
  const extension = imageExtension(input.blob.type)
  await ensureStorageCapacity(input.blob)

  const clientUploadId = crypto.randomUUID()
  const photo: QueuedCapturePhoto = {
    batchId: input.batch.batchId,
    groupId: input.groupId,
    clientUploadId,
    capturedAt: new Date(input.capturedAt).toISOString(),
    filename: `${clientUploadId}.${extension}`,
    blob: input.blob,
  }

  await runTransaction([BATCHES_STORE, PHOTOS_STORE], "readwrite", (transaction) => {
    transaction.objectStore(BATCHES_STORE).put({ accountId, ...input.batch })
    transaction.objectStore(PHOTOS_STORE).add({ accountId, ...photo })
  })
  return photo
}

export async function acknowledgePhoto(accountId: string, clientUploadId: string): Promise<void> {
  requireIdentifier(accountId, "account ID")
  requireIdentifier(clientUploadId, "client upload ID")
  await runTransaction(PHOTOS_STORE, "readwrite", (transaction) => {
    transaction.objectStore(PHOTOS_STORE).delete([accountId, clientUploadId])
  })
}

/**
 * Permanently drop a single queued photo the user chose to discard after the
 * server hard-rejected it. Mirrors acknowledgePhoto's delete on purpose — same
 * mechanism, different intent (user-initiated loss, not a confirmed upload).
 */
export async function discardPhoto(accountId: string, clientUploadId: string): Promise<void> {
  requireIdentifier(accountId, "account ID")
  requireIdentifier(clientUploadId, "client upload ID")
  await runTransaction(PHOTOS_STORE, "readwrite", (transaction) => {
    transaction.objectStore(PHOTOS_STORE).delete([accountId, clientUploadId])
  })
}

export async function listPendingPhotos(
  accountId: string,
  batchId?: string,
  groupId?: string,
): Promise<QueuedCapturePhoto[]> {
  requireIdentifier(accountId, "account ID")
  if (groupId && !batchId) throw new TypeError("A batch ID is required when filtering by group")

  return runTransaction(PHOTOS_STORE, "readonly", async (transaction) => {
    const stored = await requestResult<StoredQueuedCapturePhoto[]>(
      transaction
        .objectStore(PHOTOS_STORE)
        .index(PHOTOS_BY_ACCOUNT_BATCH_GROUP_TIME)
        .getAll(photoRange(accountId, batchId, groupId)),
    )
    return orderQueuedPhotos(stored.map(publicPhoto))
  })
}

/** Enumerate durable batch metadata for one trusted account, newest UUID first. */
export async function listQueuedBatches(accountId: string): Promise<CaptureQueueBatch[]> {
  requireIdentifier(accountId, "account ID")

  return runTransaction(BATCHES_STORE, "readonly", async (transaction) => {
    const stored = await requestResult<StoredCaptureQueueBatch[]>(
      transaction
        .objectStore(BATCHES_STORE)
        .getAll(IDBKeyRange.bound([accountId, ""], [accountId, HIGHEST_KEY_PART])),
    )
    return stored.map(publicBatch).reduce<CaptureQueueBatch[]>((ordered, candidate) => {
      const insertion = ordered.findIndex(
        (existing) => existing.batchId.localeCompare(candidate.batchId) < 0,
      )
      if (insertion === -1) ordered.push(candidate)
      else ordered.splice(insertion, 0, candidate)
      return ordered
    }, [])
  })
}

export async function restoreBatch(
  accountId: string,
  batchId: string,
): Promise<CaptureQueueSnapshot | null> {
  requireIdentifier(accountId, "account ID")
  requireIdentifier(batchId, "batch ID")

  return runTransaction([BATCHES_STORE, PHOTOS_STORE], "readonly", async (transaction) => {
    const [storedBatch, storedPhotos] = await Promise.all([
      requestResult<StoredCaptureQueueBatch | undefined>(
        transaction.objectStore(BATCHES_STORE).get([accountId, batchId]),
      ),
      requestResult<StoredQueuedCapturePhoto[]>(
        transaction
          .objectStore(PHOTOS_STORE)
          .index(PHOTOS_BY_ACCOUNT_BATCH_GROUP_TIME)
          .getAll(photoRange(accountId, batchId)),
      ),
    ])
    return storedBatch
      ? {
          batch: publicBatch(storedBatch),
          pendingPhotos: orderQueuedPhotos(storedPhotos.map(publicPhoto)),
        }
      : null
  })
}

export async function clearFinishedBatch(accountId: string, batchId: string): Promise<void> {
  requireIdentifier(accountId, "account ID")
  requireIdentifier(batchId, "batch ID")

  await runTransaction([BATCHES_STORE, PHOTOS_STORE], "readwrite", async (transaction) => {
    const photoStore = transaction.objectStore(PHOTOS_STORE)
    const keys = await requestResult<IDBValidKey[]>(
      photoStore
        .index(PHOTOS_BY_ACCOUNT_BATCH_GROUP_TIME)
        .getAllKeys(photoRange(accountId, batchId)),
    )
    for (const key of keys) photoStore.delete(key)
    transaction.objectStore(BATCHES_STORE).delete([accountId, batchId])
  })
}

export async function replaceQueuedBatch(
  accountId: string,
  staleBatchId: string,
  replacement: CaptureQueueBatch,
): Promise<void> {
  requireIdentifier(staleBatchId, "stale batch ID")
  validateAccountAndBatch(accountId, replacement)

  await runTransaction([BATCHES_STORE, PHOTOS_STORE], "readwrite", async (transaction) => {
    const batchStore = transaction.objectStore(BATCHES_STORE)
    const photoStore = transaction.objectStore(PHOTOS_STORE)
    const photos = await requestResult<StoredQueuedCapturePhoto[]>(
      photoStore
        .index(PHOTOS_BY_ACCOUNT_BATCH_GROUP_TIME)
        .getAll(photoRange(accountId, staleBatchId)),
    )

    batchStore.put({ accountId, ...replacement })
    batchStore.delete([accountId, staleBatchId])
    for (const photo of photos) {
      photoStore.put({
        ...photo,
        batchId: replacement.batchId,
        groupId: replacement.activeGroupId,
      })
    }
  })
}

async function ensureStorageCapacity(blob: Blob): Promise<void> {
  ensureQueueAvailable()
  const estimate = globalThis.navigator?.storage?.estimate
  if (!estimate) throw new CaptureQueueFullError()

  let result: StorageEstimate
  try {
    result = await estimate.call(globalThis.navigator.storage)
  } catch {
    throw new CaptureQueueFullError()
  }

  if (result.quota === undefined || result.usage === undefined) {
    throw new CaptureQueueFullError()
  }

  if (result.quota - result.usage < blob.size + RESERVED_BYTES) {
    throw new CaptureQueueFullError()
  }
}

async function runTransaction<T>(
  storeNames: string | string[],
  mode: IDBTransactionMode,
  perform: (transaction: IDBTransaction) => T | Promise<T>,
): Promise<T> {
  ensureQueueAvailable()
  const database = await openDatabase()

  try {
    let transaction: IDBTransaction
    try {
      transaction = database.transaction(storeNames, mode)
    } catch (error) {
      throw persistenceError(error)
    }
    const completion = transactionCompletion(transaction)

    let result: T | Promise<T>
    try {
      result = perform(transaction)
    } catch (error) {
      safeAbort(transaction)
      await completion.catch(() => undefined)
      throw persistenceError(error)
    }

    try {
      const [value] = await Promise.all([Promise.resolve(result), completion])
      return value
    } catch (error) {
      throw persistenceError(error)
    }
  } finally {
    database.close()
  }
}

function openDatabase(): Promise<IDBDatabase> {
  ensureQueueAvailable()

  return new Promise((resolve, reject) => {
    let request: IDBOpenDBRequest
    try {
      request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION)
    } catch (error) {
      reject(persistenceError(error))
      return
    }

    let settled = false
    request.addEventListener("upgradeneeded", () => {
      const database = request.result
      if (!database.objectStoreNames.contains(BATCHES_STORE)) {
        database.createObjectStore(BATCHES_STORE, {
          keyPath: ["accountId", "batchId"],
        })
      }
      if (!database.objectStoreNames.contains(PHOTOS_STORE)) {
        const photos = database.createObjectStore(PHOTOS_STORE, {
          keyPath: ["accountId", "clientUploadId"],
        })
        photos.createIndex(
          PHOTOS_BY_ACCOUNT_BATCH_GROUP_TIME,
          ["accountId", "batchId", "groupId", "capturedAt"],
          { unique: false },
        )
      }
    })
    request.addEventListener("success", () => {
      if (settled) {
        request.result.close()
        return
      }
      settled = true
      resolve(request.result)
    })
    request.addEventListener("error", () => {
      if (settled) return
      settled = true
      reject(persistenceError(request.error ?? new DOMException("Open failed", "UnknownError")))
    })
    request.addEventListener("blocked", () => {
      if (settled) return
      settled = true
      reject(new CaptureQueueFullError("Capture queue database upgrade was blocked"))
    })
  })
}

function transactionCompletion(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.addEventListener("complete", () => resolve(), { once: true })
    transaction.addEventListener(
      "abort",
      () =>
        reject(
          persistenceError(
            transaction.error ??
              new DOMException("Capture queue transaction aborted", "AbortError"),
          ),
        ),
      { once: true },
    )
  })
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener("success", () => resolve(request.result), { once: true })
    request.addEventListener(
      "error",
      () =>
        reject(request.error ?? new DOMException("Capture queue request failed", "UnknownError")),
      { once: true },
    )
  })
}

function safeAbort(transaction: IDBTransaction): void {
  try {
    transaction.abort()
  } catch {
    // The transaction already finished; its completion promise still owns the final result.
  }
}

function ensureQueueAvailable(): void {
  if (!globalThis.indexedDB || !globalThis.crypto?.randomUUID) {
    throw new CaptureQueueFullError()
  }
}

function persistenceError(error: unknown): unknown {
  if (error instanceof CaptureQueueFullError) return error
  return error instanceof DOMException && PERSISTENCE_ERROR_NAMES.has(error.name)
    ? new CaptureQueueFullError(error.message)
    : error
}

function imageExtension(contentType: string): "jpg" | "png" {
  if (contentType === "image/jpeg") return "jpg"
  if (contentType === "image/png") return "png"
  throw new TypeError(`Unsupported capture image type: ${contentType || "unknown"}`)
}

function validateAccountAndBatch(accountId: string, batch: CaptureQueueBatch): void {
  requireIdentifier(accountId, "account ID")
  requireIdentifier(batch.batchId, "batch ID")
  requireIdentifier(batch.activeGroupId, "active group ID")
  for (const group of batch.groups) requireIdentifier(group.groupId, "group ID")
  if (batch.transition) {
    requireIdentifier(batch.transition.groupId, "transition group ID")
    if (
      !Number.isInteger(batch.transition.expectedPhotoCount) ||
      batch.transition.expectedPhotoCount < 0
    ) {
      throw new TypeError("Capture queue transition photo count must be a non-negative integer")
    }
  }
}

function requireIdentifier(value: string, label: string): void {
  if (!value.trim()) throw new TypeError(`Capture queue ${label} is required`)
}

function photoRange(accountId: string, batchId?: string, groupId?: string): IDBKeyRange {
  return IDBKeyRange.bound(
    [accountId, batchId ?? "", groupId ?? "", ""],
    [accountId, batchId ?? HIGHEST_KEY_PART, groupId ?? HIGHEST_KEY_PART, HIGHEST_KEY_PART],
  )
}

function publicBatch({
  accountId: _accountId,
  ...batch
}: StoredCaptureQueueBatch): CaptureQueueBatch {
  return batch
}

function publicPhoto({
  accountId: _accountId,
  ...photo
}: StoredQueuedCapturePhoto): QueuedCapturePhoto {
  return photo
}

function compareQueuedPhotos(left: QueuedCapturePhoto, right: QueuedCapturePhoto): number {
  return (
    left.batchId.localeCompare(right.batchId) ||
    left.groupId.localeCompare(right.groupId) ||
    left.capturedAt.localeCompare(right.capturedAt) ||
    left.clientUploadId.localeCompare(right.clientUploadId)
  )
}

function orderQueuedPhotos(photos: QueuedCapturePhoto[]): QueuedCapturePhoto[] {
  const ordered: QueuedCapturePhoto[] = []
  for (const photo of photos) {
    const insertAt = ordered.findIndex((existing) => compareQueuedPhotos(photo, existing) < 0)
    if (insertAt === -1) ordered.push(photo)
    else ordered.splice(insertAt, 0, photo)
  }
  return ordered
}
